/**
 * Context builders for the Gateway AI Assistant.
 *
 * Two exports with very different caching lives:
 *  - buildSectionCatalog(): the STATIC section catalog. turn.js appends it
 *    to the main system prompt, which the backend marks cache_control — so
 *    its ~7KB is processed once per cache window, not on every turn.
 *  - buildGatewayContext(): the tiny PER-TURN snapshot (active section,
 *    user, license, open modal). It rides in the second, uncached system
 *    block; keep it small — everything static belongs in the catalog.
 * Heavy reads (datasources, users, etc.) are deferred to tools.
 */

import { readCollectorEditor } from "./collectorEditorBridge";

/**
 * Each section row: [label, what's on it, list_* tools that read its state,
 * resource type for the row data-attribute selector].
 * The AI reads this to answer "where is X?" precisely without guessing.
 */
const SECTION_INFO = {
  "platform-overview": {
    label: "Platform Overview",
    contains:
      "Live system dashboard with realtime metrics. Stat cards: **CPU Usage**, **Memory**, **Disk**, **Uptime** — each addressable via `[data-gw-stat=\"cpu|memory|disk|uptime\"]`. Plus cards: `[data-gw-card=\"cpu_trend\"]` (24h CPU trend chart), `[data-gw-card=\"memory_trend\"]`, `[data-gw-card=\"resource_gauges\"]` (CPU/Memory/Disk gauge chart row), `[data-gw-card=\"system_info\"]` (date/time, timezone, system name). To read the current numbers, call `get_overview_stats` (DOM-read; only works while the user is on this page). When the user asks 'what's my CPU?', 'how much memory am I using?', 'how long has the gateway been up?', or 'highlight my CPU' — this page.",
    tools: ["get_overview_stats"],
  },
  "gateway-settings": {
    label: "Gateway Settings",
    contains:
      "Workspace-wide gateway settings — NOT per-user profile. Three cards: " +
      "**Routing** (`Homepage Redirect`: the path the root URL `/` redirects to, e.g. `/editor` " +
      "or `/<project>/<page>`), **Sidebar** (a checkbox per section deciding which ones appear in " +
      "the left navigation — hiding one only declutters the nav, the page stays reachable by search " +
      "and direct link) and **Access Control** (`Public Runtime Pages` toggle: when ON, " +
      "published runtime pages are visible without login; when OFF, visitors hit the login screen). " +
      "When the user says 'gateway settings', 'entry point URL', 'default landing page', 'homepage', " +
      "'where do users land', 'should the runtime be public/private', or asks to show/hide a section " +
      "in the sidebar — this is the page.",
    tools: ["get_workspace_settings"],
  },
  "plc-overview": {
    label: "PLC Overview",
    contains:
      "The normal starting point for virtual PLC work. It presents one beginner journey: add a Raspberry Pi or industrial-PC runtime, connect I/O, open or create a Structured Text application, then deploy and run it. It also summarizes runtimes needing attention and recent deployment state. Detailed controller evidence, rollout signing, protocol-server internals, and compatibility gates are intentionally under Advanced diagnostics. Missing or partial evidence remains unavailable and must never be described as healthy.",
    tools: ["list_edge_runtimes", "list_plc_controllers", "get_plc_controller_output_delivery", "list_plc_fleet_rollouts", "get_modbus_server_status", "get_opcua_server_role_status", "get_s7comm_server_status"],
  },
  "plc-runtime-fleet": {
    label: "PLC Runtimes",
    contains:
      "The single normal workspace for adding and managing the computers that run virtual PLC applications. The list gives each runtime one Open action leading to Overview, Applications, I/O & connections, and Maintenance. Runtime Overview shows the latest bounded device-reported CPU, memory, disk, and Control Seat uptime separately from PLC execution metrics (running applications, scan duration, and overruns); offline values are last-reported evidence, not live health. Enrollment should ask only for a friendly runtime name and a reachable Control Seat URL; synchronization limits and identity details stay under Advanced enrollment settings. Connections start with a protocol the selected runtime actually advertises, then collect its device address and tag mapping. If workspace connection setup is unavailable, stop before creating tags and direct a workspace administrator to finish it. The FR201 has a built-in industrial serial port but no FR202 terminal I/O; serial-port detection does not prove its EEPROM electrical mode or field wiring. The exact Compulab IOT-LINK profile exposes two fixed RS-485 ports and three digital channels; use its reported port A or B and never substitute modem/debug ports or an unsupported local API path. GPIO is one connection kind, not a separate competing concept. Applications means PLC programs deployed to that runtime. Dedicated controller evidence remains a support-only deep link and must not be presented as a required setup step. Runtime hosts and controller execution boundaries are never merged into host records. The controller support view is implemented by Virtual PLC Controllers and exposes desired versus authenticated actual state, freshness, isolation, application/connector names, resource ceilings, warnings, history, support evidence, and redacted output stages. Start, Stop, Restart, Reset, and eligible Retry remain disabled whenever their exact executable lifecycle capability is unavailable. propose_plc_controller_lifecycle opens the exact backend review; AI cannot confirm or queue it. Restart is eligible only when the backend explicitly advertises authenticated atomic cutover for a homogeneous connector-free PROGRAM-project set. RETAIN requires the signed controller-project-retained-state-v1 capability. Preserve and reject_if_present are the safe defaults. Manual cold reset additionally requires controller-project-retained-reset-v1. Manual active-force clear has its own independent post-preview acknowledgement. PLC AI always proposes preserve plus reject_if_present and must never request or expose a Retry source identity. Device connections and tag connector configuration stay in their canonical Devices/Tags surfaces. AI may guide or prefill a review, but cannot confirm, queue, start, stop, reset, clear forces, or claim an unavailable feature works.",
    tools: ["get_plc_capabilities", "list_edge_runtimes", "get_edge_connectors", "inspect_edge_runtime_tags", "list_edge_gpio_bindings", "propose_edge_gpio_binding", "propose_edge_modbus_rtu_connection", "list_plc_controllers", "get_plc_controller_events", "get_plc_controller_output_delivery", "get_plc_controller_support", "propose_plc_controller_draft_change", "propose_plc_controller_lifecycle"],
    resources: ["edge_gateways", "plc_controllers"],
  },
  "plc-compatibility": {
    label: "Compatibility",
    contains:
      "The canonical PLC Capabilities & Roadmap manifest, organized as an advanced compatibility surface. It separates executable features from configuration-only, hardware-lab-gated, and planned work; shows delivery phases, dependencies, limitations, and release gates; and includes live EtherCAT/PROFIBUS host readiness without claiming those protocol backends exist. Always read this manifest before answering whether a PLC language, protocol, runtime, simulation, deployment, or fleet feature is supported.",
    tools: ["get_plc_capabilities", "get_plc_fieldbus_readiness"],
  },
  "plc-deployments": {
    label: "Applications",
    contains:
      "Managed deployment signing and the PLC Fleet Rollouts workflow. Signing status exposes public key IDs and policy readiness only; private/public key bytes, secret references, preview tokens, and confirmation digests never enter AI context. Signing must be active before first enrollment. Managed key creation or rotation is blocked while any runtime is enrolled because non-destructive trust redistribution is not implemented; AI must state the release blocker and never suggest removing production runtimes as a workaround. Fleet rollout remains staged and target-leased with immutable signed desired and rollback artifacts, explicit confirmation, command receipts, independent exact-artifact health gates, pause/cancel, and rollback controls. AI may inspect or prefill a rollout proposal, but cannot configure signing, confirm a digest, or actuate a rollout.",
    tools: [
      "get_plc_deployment_signing_policy",
      "list_plc_fleet_candidates",
      "list_plc_fleet_rollouts",
      "get_plc_fleet_rollout",
      "get_plc_fleet_rollout_events",
      "propose_plc_fleet_rollout",
      "propose_plc_fleet_control",
    ],
  },
  "plc-protocol-services": {
    label: "Servers",
    contains:
      "The Modbus TCP listener is disabled by default. " +
      "The existing PLC Protocol Servers workflow under its job-oriented name: native inbound industrial protocol endpoints. Modbus TCP can target the Local gateway or an enrolled runtime. Edge execution requires a fresh online snapshot with connector-config-v1 plus modbus-tcp-server and uses immutable signed modbus-tcp-server-v1 revisions. The UI distinguishes desired, applied, listening, stale/offline, and divergence; only exact device-confirmed revision/digest plus Running proves listening. AI may inspect and prefill a draft but cannot create the preview, confirm, queue, start, stop, retry, or roll back this server. The bounded S7Comm DB-scalar compatibility server executes only on local connector builds; cloud stores disabled configuration only, does not open or forward a listener. S7Comm has no Siemens identity/SZL, application authentication, encryption, integrity, or replay protection. Its write audit is reserved before dispatch and protocol success waits for accepted completion; if post-dispatch completion fails, the wire receives failure while the reservation and possibly accepted command remain pending/ambiguous because an external connector cannot share one atomic transaction. For S7Comm use reconciled as overall truth: desired_equals_applied is durable-only and runtime_matches_applied is executable-runtime truth. Public integrity truth distinguishes unsealed rollback-ineligible legacy-path-v0 from keyed-binding-v1; never expose a key fingerprint, and treat key mismatch as Retry/restore-secret rather than repair. Degraded means the latest audit-sink failure is newer than the latest recorded audit; the listener stays active while affected writes fail closed. Diagnostics has fixed counters and bounded public tag paths. Structured source/edge disconnect is stale, other non-Good state remains non_good, and diagnostic text is never parsed to infer the cause. Actor-attributed configuration proposal/preview/confirmation/apply/reject and enable/disable audit is available as bounded read-only summaries; proposal identities, configuration bodies, and secrets are excluded (tag paths and their values are the operator's own plant data and are not withheld). The operations-audit gate remains pending for per-event network rate-limit/audit-sink operations. Read-only revision history exposes bounded summaries and rollback eligibility only. Exact semantic rollback preview/confirmation is GUI-only and appends a higher revision without rewriting history. Only repair_required=true exposes the reviewed GUI-only authenticated repair that disables the listener, writes a new empty desired/applied revision, and preserves corrupt rows as evidence; transient/unclassified failures offer Retry and AI cannot invoke repair. The OPC UA server role has a reviewed secure desired-namespace workflow but remains deliberately non-executable. A confirmed revision advances Desired only; Applied stays absent, all reconciliation flags stay false, and the independently Closed listener is not an applied revision. AI may propose only public endpoint/namespace URI/identity/source-network/resource-limit fields; namespace mappings and certificate/key/trust references are human-only, and AI cannot confirm or apply. Generic form snapshots expose only PKI configured booleans and no mapping values; generic form mutations, clicks, Preview, and submission fail closed. Use each protocol's configuration and status tools before describing saved versus desired, applied, listening, or release-qualified state.",
    tools: [
      "get_plc_capabilities",
      "get_modbus_server_config",
      "get_modbus_server_status",
      "list_edge_runtimes",
      "get_edge_connectors",
      "inspect_edge_runtime_tags",
      "propose_edge_modbus_tcp_server",
      "get_opcua_server_role_config",
      "get_opcua_server_role_status",
      "propose_opcua_server_role_change",
      "get_s7comm_server_config",
      "get_s7comm_server_status",
      "get_s7comm_server_revisions",
      "get_s7comm_server_config_audit",
    ],
    resources: ["modbus_server", "opcua_server_role", "s7comm_server"],
  },
  "accounts-settings": {
    label: "Account Settings (per-user profile)",
    contains:
      "Per-USER profile and password — first/last name, email, phone, department, location, " +
      "avatar, change-password. NOT workspace-wide settings; for that see `gateway-settings`. " +
      "**Inline-form page**: edits go through `update_my_profile` directly, NOT click_element / " +
      "set_field / submit_open_form. Read current values via `get_me`. Password changes are " +
      "deliberately NOT routed through the AI — direct the user to the page's Password card.",
    tools: ["get_me"],
  },
  "user-management": {
    label: "Users & Groups",
    contains:
      "User accounts, pending invites, and groups (teams/shifts/departments). " +
      "Three lists on one page: users, invites, groups.",
    tools: ["list_users", "list_groups", "get_group"],
    resources: ["users", "invites", "groups"],
  },
  "projects-list": {
    label: "Projects",
    contains: "Project workspaces (each project owns its own pages and flows).",
    tools: ["list_projects"],
    resources: ["projects"],
  },
  "database-connections": {
    label: "Database connections",
    contains:
      "Persistent DB connections (postgresql, mysql, influxdb, prometheus). Click a row to browse its schemas/tables/columns.",
    tools: [
      "list_datasources",
      "describe_datasource",
      "ping_datasource",
      "list_db_schemas",
      "list_db_tables",
      "list_db_columns",
      "get_supported_connector_types",
    ],
    resources: ["datasources"],
  },
  "api-connections": {
    label: "API profiles (HTTP polling)",
    contains:
      "HTTP polling profiles — periodic GET/POST to a URL with optional auth. " +
      "Each profile can have tags that pull values from the response body.",
    tools: ["list_api_profiles", "list_profile_tags"],
    resources: ["api_profiles"],
  },
  "api-credentials": {
    label: "API credentials",
    contains:
      "Reusable credentials for outbound HTTP nodes in flows (OAuth tokens, basic auth, bearer tokens, api keys).",
    tools: ["list_credentials", "test_credential"],
    resources: ["credentials"],
  },
  "oauth-apps": {
    label: "OAuth apps",
    contains:
      "OAuth client app registrations per provider (Slack, Google, Notion, GitHub, Microsoft). Holds client_id/client_secret used by credentials.",
    tools: ["list_oauth_providers", "list_oauth_apps", "get_oauth_app"],
    resources: ["oauth_apps"],
  },
  "mqtt-brokers": {
    label: "MQTT brokers",
    contains:
      "MQTT broker connections with their topic subscriptions listed inline under each broker.",
    tools: [
      "list_mqtt_brokers",
      "get_mqtt_broker_state",
      "list_mqtt_subscriptions",
    ],
    resources: ["mqtt_brokers", "mqtt_subscriptions"],
  },
  "modbus-devices": {
    label: "Modbus",
    contains:
      "Modbus TCP and serial RTU client/master device connections. The runtime status says whether this target can execute I/O or only store configuration. Each normal tag selects a device, function/table, strict zero-based address, codec, scaling, and output verification. Readback proves the returned point value; device response is for devices that accept writes but do not answer reads and proves only request acceptance, never physical actuation. Integer registers with fractional scaling require a Number tag. Use the live capability manifest before making a support or qualification claim.",
    tools: [
      "get_plc_capabilities",
      "get_modbus_runtime_status",
      "list_modbus_devices",
      "list_modbus_serial_ports",
      "list_edge_runtimes",
      "get_edge_connectors",
      "inspect_edge_runtime_tags",
      "propose_edge_modbus_rtu_connection",
    ],
    resources: ["modbus_devices"],
  },
  "opc-connections": {
    label: "OPC UA",
    contains:
      "OPC UA client connections used to browse, subscribe, read, and write normal unified tags. Server endpoints, security mode/policy, identities, connection state, and reconnect controls live here. This is a client/source driver; the separate inbound OPC UA server role is reported and drafted on Servers but is not executable.",
    tools: [
      "get_plc_capabilities",
      "list_opcua_servers",
      "get_opcua_server_state",
    ],
    resources: ["opcua_servers"],
  },
  "historian-providers": {
    label: "Historians",
    contains:
      "Configured historian backends — the built-in Control Seat local historian plus external ones like TimescaleDB/Tiger Cloud — where a tag's history is stored. Each tag/MQTT-subscription picks one. Rows test their connection live.",
    tools: ["list_historian_providers", "test_historian_provider"],
    resources: ["historian_providers"],
  },
  "api-ai-settings": {
    label: "AI settings",
    contains:
      "LLM provider configurations (OpenAI / Anthropic / xAI / Google / Ollama / custom) used by this AI assistant and the editor's AI. Has two cards: a 'My Preferences' card (per-user provider/model override) and a 'Providers' card (the actual provider list).",
    tools: ["list_ai_providers", "get_ai_preference", "list_known_models"],
    resources: ["ai_providers"],
  },
  "alarms-management": {
    label: "Alarm Management",
    contains:
      "Live alarm stream — currently active alarms with state (active/clear), severity (critical/high/medium/low), source path, acknowledged flag, activation timestamp. Each row has selector `[data-gw-resource=\"live_alarms\"][data-gw-id=\"<alarm_id>\"]`. To list current alarms, use `list_alarms` (returns RULES — for live state the WS-driven page is authoritative; the list still shows rule definitions). Acknowledge via `acknowledge_alarm({id})` through `request_confirmation` — it is not a gated kind, so it applies immediately.",
    tools: ["list_alarms"],
    resources: ["live_alarms"],
  },
  "alarms-notifications": {
    label: "Alarm Notifications (alarm rules CRUD)",
    contains:
      "Configure alarm RULES — per-tag threshold definitions plus notification routing (which users/groups get paged). Each rule has: tag_id, name, mode (`above`/`below`/`between`/`outside_range`), setpoint (+ setpoint_high for between/outside_range), severity (`critical`/`high`/`medium`/`low`), message, on_delay_ms, off_delay_ms, notify_all, notify_user_ids, notify_group_ids, enabled. Modify with `propose_alarm` (create), `update_alarm({id, ...patch})` (toggle enabled, change threshold/severity), `delete_alarm({id})`. Recipients via `list_alarm_users`. Always resolve tag paths via `list_tags` first — alarms are per-tag. Row selector: `[data-gw-resource=\"alarm_rules\"][data-gw-id=\"<id>\"]`.",
    tools: ["list_alarms", "list_alarm_users", "list_tags"],
    resources: ["alarm_rules"],
  },
  "custom-collectors": {
    label: "Custom Connectors",
    contains:
      "Operator-authored Python scripts the gateway runs on a schedule; each named value they return lands in an ordinary tag with the ordinary historian. Use when a source has no driver — a public API, a CSV, a scraped page. `list_collectors` reads the list, its cadence and its last error. Clicking a row (or **New collector**) opens the editor, whose Script block has a **Generar con IA** button that raises this drawer: while that editor is open, `get_collector_script` reads the live script and `set_collector_script` writes a new one into it. Filling the editor is never saving — the operator reads the code, presses Save, then Run now (dry run), and only then enables the collector. Row selector: `[data-gw-resource=\"custom_collectors\"][data-gw-id=\"<id>\"]`.",
    tools: ["list_collectors", "get_collector_script", "set_collector_script"],
    resources: ["custom_collectors"],
  },
  "platform-license": {
    label: "License (on-prem only)",
    contains:
      "On-prem license state — customer, edition, expiration countdown, gateway install_id (the activation key), and recent audit events. Admins upload a renewed `license.json` here (drag-drop) or click 'Activate online' to fetch one from controlseat.com. **Hidden on cloud SaaS** (where licensing is disabled). When `mode != \"licensed\"`, runtime endpoints (live tag I/O, historian, runtime page rendering) return HTTP 402 — that's the page to direct the user to when they ask 'why isn't my data flowing?' If the user asks 'when does my license expire?' or 'how many days are left?', call `get_license` (or `get_system_info` for non-admins).",
    tools: ["get_license", "get_system_info"],
  },
  "platform-updates": {
    label: "Updates (on-prem only)",
    contains:
      "Shows the gateway's current build version (`current`, `commit`, `build_time`) and lets an admin click 'Check for updates' to make a one-shot outbound call to controlseat.com for the latest release. **Hidden on cloud SaaS**. The gateway never phones home on its own — this button is the ONLY outbound call. Use `get_system_info` (any user) for the current version, or `check_for_updates` (admin) when the user explicitly asks 'is there an update?'.",
    tools: ["get_system_info", "check_for_updates"],
  },
  "platform-tls": {
    label: "Web Server / TLS (on-prem only)",
    contains:
      "On-prem HTTPS certificate management. Admins upload a PEM cert + private key; the gateway hot-swaps the active cert without a restart. Shows cert subject, SAN/DNS names, validity dates, and SHA-256 fingerprint. **Hidden on cloud SaaS** (TLS terminated at the load balancer). The private key is encrypted with AES-256-GCM at rest and never leaves this gateway. Use `get_tls_config` to read the current state.",
    tools: ["get_tls_config"],
  },
};

const SECTION_LABELS = Object.fromEntries(
  Object.entries(SECTION_INFO).map(([id, info]) => [id, info.label])
);
Object.assign(SECTION_LABELS, {
  "plc-controllers": "Runtime Fleet · Controllers",
  "plc-edge-runtimes": "Runtime Fleet · Runtime hosts",
  "plc-fleet-rollouts": "Deployments",
  "plc-capabilities": "Compatibility",
  "plc-protocol-servers": "Servers",
});

function describeSection(sectionId) {
  if (!sectionId) return "(unknown)";
  return SECTION_LABELS[sectionId] || sectionId;
}

/**
 * Best-effort sniff of the open modal at turn-start. Lets the AI know it
 * has a form to work with without first calling list_form_fields. The
 * fields-level introspection still goes through that tool — this is just
 * the "is there a form open" hint.
 */
function describeOpenModal() {
  if (typeof document === "undefined") return null;
  const modal =
    document.querySelector(".gwOverlay .gwModal") || document.querySelector(".gwModal");
  if (!modal) return null;
  const title =
    modal.querySelector(".gwModalTitle")?.textContent?.trim() || "(untitled modal)";
  return title;
}

/**
 * Static catalog of every gateway section: what's on it, which tools read
 * its state, and its row selectors. Appended to the cached system prompt by
 * turn.js — do NOT emit this per turn.
 */
export function buildSectionCatalog() {
  const lines = ["## Sections and what's on each page"];
  lines.push(
    "Use the section id verbatim with `navigate_to` and `highlight_element`'s `sectionId`. " +
    "When the user asks 'where is the X page?', highlight `[data-gw-nav-item=\"<sectionId>\"]` " +
    "instead of navigating. Resource rows on each page expose " +
    "`[data-gw-resource=\"<type>\"][data-gw-id=\"<id>\"]` " +
    "(or `[data-gw-name=\"<name>\"]`) — use those to highlight a SPECIFIC row."
  );
  for (const [id, info] of Object.entries(SECTION_INFO)) {
    const tools = info.tools && info.tools.length > 0 ? ` Tools: \`${info.tools.join("`, `")}\`.` : "";
    const resources = info.resources && info.resources.length > 0
      ? ` Row selectors: \`[data-gw-resource="${info.resources.join('"], [data-gw-resource="')}"]\`.`
      : "";
    lines.push(`  - \`${id}\` — **${info.label}**: ${info.contains}${tools}${resources}`);
  }
  lines.push("");
  lines.push(
    "**How to see what's on screen**: backend state via `list_*` / `describe_*` / `get_*` tools. Form state via `list_form_fields` (open modal title, fields, submit-button enabled/disabled). Dropdown options via `list_select_options`. Ground every action in a fresh read — don't guess."
  );
  return lines.join("\n");
}

/**
 * @param {Object} opts
 * @param {string} [opts.activeSection]   The active gateway section id.
 * @param {Object} [opts.user]            { id, username, email, role, permissions }
 * @param {Object} [opts.license]         { licensingEnabled, mode, daysRemaining, expiresAt, customer, edition }
 */
export function buildGatewayContext({ activeSection, user, license, skills } = {}) {
  const lines = ["## Current gateway context"];
  lines.push(`- Active section: ${describeSection(activeSection)} (id: \`${activeSection || "?"}\`)`);
  if (user) {
    const role = user.role || (Array.isArray(user.permissions) && user.permissions.includes("admin") ? "admin" : "user");
    const name = user.username || user.email || "(unknown)";
    lines.push(`- User: ${name} (role: ${role})`);
  }
  if (license) {
    lines.push(`- Deployment: ${license.licensingEnabled ? "on-prem (licensing enabled)" : "cloud SaaS (licensing disabled — License/Updates/TLS pages are hidden)"}`);
    if (license.licensingEnabled && license.mode) {
      const days = typeof license.daysRemaining === "number" ? license.daysRemaining : null;
      const tail =
        license.mode === "licensed" && days !== null
          ? ` (${days} day${days === 1 ? "" : "s"} remaining${license.expiresAt ? `, expires ${license.expiresAt.slice(0, 10)}` : ""})`
          : license.mode === "expired" && days !== null && days < 0
            ? ` (expired ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ago — runtime endpoints return HTTP 402; admins should renew at Platform → License)`
            : license.mode === "unlicensed"
              ? " (no license loaded — runtime endpoints return HTTP 402; admins should activate at Platform → License)"
              : "";
      const meta = [
        license.customer ? `customer="${license.customer}"` : null,
        license.edition ? `edition="${license.edition}"` : null,
      ].filter(Boolean).join(", ");
      lines.push(`- License: **${license.mode}**${tail}${meta ? `; ${meta}` : ""}`);
    }
  }
  const openModal = describeOpenModal();
  if (openModal) {
    lines.push(
      `- Open modal: **${openModal}** — call \`list_form_fields\` for the full snapshot (fields, kinds, current values, submit-button state).`
    );
  }
  // The collector editor holds an unsaved Python script that no API can read,
  // so the model is told the moment one is open rather than being left to
  // discover it from the modal title.
  const collector = readCollectorEditor();
  if (collector) {
    lines.push(
      `- Open custom connector: **${collector.name || "(unnamed)"}** — ${collector.script_lines} line(s) of Python, ` +
      `${collector.outputs.length} output(s) declared. Read it with \`get_collector_script\` and rewrite it with ` +
      "`set_collector_script`; `list_form_fields` / `set_field` do NOT reach the script box."
    );
  }
  // Same list run_skill's schema enumerates this turn (BETA-31) — one fetch
  // in turn.js, two uses, mirroring the Go plant assistant's prefetch.
  if (Array.isArray(skills) && skills.length > 0) {
    const entries = skills
      .map((s) => (s.whenToUse ? `${s.name} — ${s.whenToUse}` : s.name))
      .join("; ");
    lines.push(`- Skills (${skills.length}): ${entries}`);
  }
  lines.push(
    "The full section catalog (ids, contents, tools, row selectors) is in your system prompt under 'Sections and what's on each page'."
  );
  return lines.join("\n");
}

export default buildGatewayContext;
